import React, { useEffect, useState } from 'react';
import * as PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import cn from 'classnames';
import some from 'lodash/some';

import Button from '../../Button';
import Loader from '../../common/Loader';
import VisitInfoForm from './VisitInfoForm';
import { getVisitTypeIdWithCode } from '../../../utils/getVisitTypeIdWithCode';
import { getVisitFormOptions } from '../utils/getVisitFormData';
import { useVisitInfoForm } from '../utils/useVisitInfoFormState';
import { convertUTCVisitTime, isValidTime } from '../utils/visitTime';
import { PROFILE_FIELDS_TYPES } from '../../../constants/formFields';
import { VIRTUAL_VISIT_TYPE_CODE } from '../../../constants';
import {
  selectVirtualServiceByLocationId,
  selectVisitTypesList,
} from '../../../redux/appointmentForm/selectors';

import { enhance } from './enhance';
import OverlapModal from '../OverlapModal';
import MetaHelmet from '../../AppHelmet/MetaHelmet';
import { usePageView } from '../../../hocs/usePageView';

function VisitDetails({
  errors,
  pending,
  locations,
  createVisit,
  providersList,
  formatMessage,
  fetchProviders,
  clearVisitTypes,
  fetchVisitTypes,
  reasonTypesList,
  clearCreateVisit,
  fetchReasonTypes,
  loadingProviders,
  loadingReasonTypes,
  params: { userId },
}) {
  const {
    handleFormChange,
    formState,
    clearForm,
    isValid,
  } = useVisitInfoForm();

  const { START_TIME, COPAY } = PROFILE_FIELDS_TYPES;

  const {
    providerId,
    reasonNote,
    reasonTypeId,
    serviceId,
    locationId,
    copay,
    date,
    startTime,
  } = formState;

  const initErrorsState = {
    [COPAY]: false,
    [START_TIME]: false,
  };

  const [errorState, setError] = useState(initErrorsState);

  const services = useSelector(selectVirtualServiceByLocationId(locationId));
  const allVisitTypesList = useSelector(selectVisitTypesList);

  usePageView('Visit Details');
  useEffect(() => {
    fetchProviders({ userId });
    fetchVisitTypes({ filterVisitTypeLocations: true });
    return () => {
      clearVisitTypes();
      clearCreateVisit();
    };
  }, []);

  useEffect(() => {
    if (serviceId && locationId) {
      fetchReasonTypes({
        serviceId,
        userId,
        locationId,
        visitTypeId: getVisitTypeIdWithCode(
          VIRTUAL_VISIT_TYPE_CODE,
          allVisitTypesList,
        ),
      });
    }
  }, [serviceId, allVisitTypesList, locationId]);

  useEffect(() => {
    const timeError = isValidTime(startTime, date);
    setError({ ...errorState, [START_TIME]: timeError });
  }, [startTime, date]);

  const handleCopayChange = (value, err) => {
    setError({ ...errorState, [COPAY]: err });
    handleFormChange({ [COPAY]: value });
  };

  const handleSubmit = e => {
    e.preventDefault();

    const dateTime = convertUTCVisitTime(date, startTime);

    const visit = {
      userId,
      dateTime,
      providerId,
      payload: {
        reasonNote,
        reasonTypeId,
        serviceId,
        locationId,
        copay,
      },
    };
    createVisit(visit);
  };

  if (loadingProviders) return <Loader />;

  return (
    <div styleName="section">
      <MetaHelmet title="pages.title.visitDetails" />
      <div styleName={cn('content', { disabled: pending })}>
        <form onSubmit={handleSubmit}>
          <VisitInfoForm
            {...formState}
            formatMessage={formatMessage}
            errors={errors}
            onChange={handleFormChange}
            showTimeError={errorState[START_TIME]}
            locationsList={getVisitFormOptions(locations)}
            servicesList={getVisitFormOptions(services)}
            reasonList={getVisitFormOptions(reasonTypesList)}
            loadingReasonTypes={loadingReasonTypes}
            providersList={getVisitFormOptions(providersList, true)}
            handleCopayChange={handleCopayChange}
          />

          <div styleName="btn-block">
            <Button
              type="button"
              raised
              color="accent"
              disabled={pending}
              onClick={() => clearForm()}
            >
              {formatMessage({ id: 'common.cancel' })}
            </Button>
            <Button
              className="ml3"
              type="submit"
              raised
              color="primary"
              disabled={!isValid || some(errorState) || pending}
            >
              {formatMessage({ id: 'virtualVisit.details.schedule' })}
            </Button>
          </div>
        </form>
      </div>
      <OverlapModal />
    </div>
  );
}

VisitDetails.propTypes = {
  formatMessage: PropTypes.func,
  pending: PropTypes.bool,
  errors: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
  fetchVisitTypes: PropTypes.func,
  createVisit: PropTypes.func,
  clearVisitTypes: PropTypes.func,
  clearCreateVisit: PropTypes.func,
  fetchProviders: PropTypes.func,
  fetchReasonTypes: PropTypes.func,
  providersList: PropTypes.array,
  loadingProviders: PropTypes.bool,
  locations: PropTypes.array,
  reasonTypesList: PropTypes.array,
  loadingReasonTypes: PropTypes.bool,
  params: PropTypes.object,
};

export default enhance(VisitDetails);



// WEBPACK FOOTER //
// ./src/components/VirtualVisit/VisitDetails/index.jsx